import { useState, useCallback, useMemo } from 'react'
import { metricThresholdsConfig, MetricThresholdsConfig, MetricThreshold } from '@/config'

const STORAGE_KEY = 'optimizer_metric_thresholds'

/**
 * Hook to manage user-customizable metric thresholds for dashboard color coding.
 * Uses a 3-tier system (red/yellow/green) with data-driven defaults.
 * Persists to localStorage so user preferences survive across sessions.
 */
export function useMetricThresholds() {
  // Load initial state from localStorage or use defaults
  const [thresholds, setThresholdsState] = useState<MetricThresholdsConfig>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<MetricThresholdsConfig>
        // Merge with defaults to ensure all fields exist
        return {
          sharpe: { ...metricThresholdsConfig.sharpe, ...parsed.sharpe },
          sortino: { ...metricThresholdsConfig.sortino, ...parsed.sortino },
          winRate: { ...metricThresholdsConfig.winRate, ...parsed.winRate },
          drawdown: { ...metricThresholdsConfig.drawdown, ...parsed.drawdown },
          trades: { ...metricThresholdsConfig.trades, ...parsed.trades },
          profitFactor: { ...metricThresholdsConfig.profitFactor, ...parsed.profitFactor },
          objectiveScore: { ...metricThresholdsConfig.objectiveScore, ...parsed.objectiveScore },
        }
      }
    } catch {
      // Invalid JSON, use defaults
    }
    return metricThresholdsConfig
  })

  // Update a single metric's thresholds
  const updateMetricThreshold = useCallback((
    metric: keyof MetricThresholdsConfig,
    newThreshold: Partial<MetricThreshold>
  ) => {
    setThresholdsState(prev => {
      const updated = {
        ...prev,
        [metric]: { ...prev[metric], ...newThreshold }
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
      return updated
    })
  }, [])

  // Update all thresholds at once
  const setThresholds = useCallback((newThresholds: MetricThresholdsConfig) => {
    setThresholdsState(newThresholds)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newThresholds))
  }, [])

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setThresholdsState(metricThresholdsConfig)
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  // Check if thresholds have been customized
  const isCustomized = useMemo(() => {
    return JSON.stringify(thresholds) !== JSON.stringify(metricThresholdsConfig)
  }, [thresholds])

  return {
    thresholds,
    updateMetricThreshold,
    setThresholds,
    resetToDefaults,
    isCustomized,
    defaults: metricThresholdsConfig,
  }
}

/**
 * Get color class for a metric value based on 3-tier thresholds (red/yellow/green).
 * For drawdown, lower is better (inverted logic).
 */
export function getMetricColorFromThresholds(
  value: number | undefined,
  metric: keyof MetricThresholdsConfig,
  thresholds: MetricThresholdsConfig
): string {
  if (value == null) return 'text-gray-500'

  const t = thresholds[metric]

  // Drawdown uses inverted logic (lower is better)
  if (metric === 'drawdown') {
    if (value <= t.green) return 'text-green-400'
    if (value <= t.yellow) return 'text-yellow-400'
    return 'text-red-400'
  }

  // All other metrics: higher is better
  if (value >= t.green) return 'text-green-400'
  if (value >= t.yellow) return 'text-yellow-400'
  return 'text-red-400'
}

/**
 * Get progress bar color class for a metric value (3-tier system).
 */
export function getMetricBarColorFromThresholds(
  value: number | undefined,
  metric: keyof MetricThresholdsConfig,
  thresholds: MetricThresholdsConfig
): string {
  if (value == null) return 'bg-gray-500'

  const t = thresholds[metric]

  // Drawdown uses inverted logic (lower is better)
  if (metric === 'drawdown') {
    if (value <= t.green) return 'bg-green-500'
    if (value <= t.yellow) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  // All other metrics: higher is better
  if (value >= t.green) return 'bg-green-500'
  if (value >= t.yellow) return 'bg-yellow-500'
  return 'bg-red-500'
}

/**
 * Get trend indicator for a metric value (3-tier system).
 */
export function getMetricTrendIndicator(
  value: number | undefined,
  metric: keyof MetricThresholdsConfig,
  thresholds: MetricThresholdsConfig
): string {
  if (value == null) return ''

  const t = thresholds[metric]

  // Drawdown uses inverted logic
  if (metric === 'drawdown') {
    if (value <= t.green) return '↓'
    if (value <= t.yellow) return '→'
    return '↑'
  }

  // Trades uses check marks instead of arrows
  if (metric === 'trades') {
    if (value >= t.green) return '✓'
    if (value >= t.yellow) return '~'
    return '!'
  }

  // All other metrics
  if (value >= t.green) return '↑'
  if (value >= t.yellow) return '→'
  return '↓'
}
